<template>
  <div class="arp-fill">
    <div class="arp-fill__row">
      <b-field
        label="Fornecedor"
        class="arp-fill__fornecedor"
        :type="!draft.fornecedorId && touched ? 'is-danger' : ''"
      >
        <b-select
          expanded
          placeholder="Selecione o fornecedor"
          :value="draft.fornecedorId"
          :disabled="fornecedorBloqueado"
          @input="onFornecedor"
        >
          <option v-for="f in fornecedores" :key="f.id" :value="f.id">
            {{ f.nome }}
          </option>
        </b-select>
      </b-field>

      <b-field label="Número" class="arp-fill__numero">
        <b-input :value="draft.numero" maxlength="50" @input="v => patch({ numero: v })" />
      </b-field>

      <b-field label="Ano" class="arp-fill__ano">
        <b-input
          :value="draft.ano"
          type="number"
          min="1999"
          max="9999"
          step="1"
          :disabled="anoBloqueado"
          @input="onAno"
        />
      </b-field>

      <b-field v-if="exigeAssinatura" label="Data de assinatura" class="arp-fill__assinatura">
        <b-datepicker
          :value="toDate(draft.dataAssinatura)"
          editable
          icon="calendar-today"
          :max-date="hoje"
          placeholder="Assinatura"
          @input="v => patch({ dataAssinatura: toIso(v) })"
        />
      </b-field>

      <DateRangePicker
        class="arp-fill__vigencia"
        label="Vigência"
        placeholder="Selecionar período"
        date-limit="any"
        dropdown-align="right"
        :show-presets="false"
        :max-span-months="12"
        :value="vigenciaRange"
        @input="onVigencia"
      />

      <b-field label="Permitir carona" class="arp-fill__carona">
        <b-switch :value="draft.permitirCarona" @input="v => patch({ permitirCarona: v })">
          {{ draft.permitirCarona ? 'Sim' : 'Não' }}
        </b-switch>
      </b-field>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import DateRangePicker from '@/components/DateRangePicker/DateRangePicker';
import {
  dateToFormIsoString,
  normalizeAnoArpInput,
  parseIsoDateLocal,
  startOfToday,
} from '../helpers';

export default {
  name: 'ArpFillReserva',
  components: { DateRangePicker },
  data() {
    return { touched: false };
  },
  computed: {
    ...mapGetters({
      draft: 'obterArpDraft',
      journey: 'obterArpJourney',
      fornecedoresArp: 'obterArpFornecedores',
    }),
    /** Cadastro reserva: só quem aderiu ao preço do vencedor. */
    fornecedores() {
      const aderentes = (this.fornecedoresArp || []).filter(f => f.papel === 'adesao');
      const titularId = Number(this.draft.fornecedorId);
      if (
        Number.isFinite(titularId) &&
        titularId > 0 &&
        !aderentes.some(f => Number(f.id) === titularId)
      ) {
        return [
          {
            id: titularId,
            nome: this.draft.fornecedorNome || `Fornecedor #${titularId}`,
            papel: 'adesao',
          },
          ...aderentes,
        ];
      }
      return aderentes;
    },
    isEdit() {
      return this.journey.mode === 'edit';
    },
    isRetificacao() {
      return this.isEdit && this.journey.isRascunho === false;
    },
    fornecedorBloqueado() {
      return this.isEdit;
    },
    anoBloqueado() {
      return this.isRetificacao;
    },
    exigeAssinatura() {
      return this.isRetificacao;
    },
    hoje() {
      return startOfToday();
    },
    vigenciaRange() {
      return {
        start: parseIsoDateLocal(this.draft.vigenciaInicio),
        end: parseIsoDateLocal(this.draft.vigenciaFim),
      };
    },
  },
  created() {
    this.carregarArpFornecedores();
    this.touched = true;
  },
  methods: {
    ...mapActions(['carregarArpFornecedores', 'atualizarArpDraft']),
    patch(payload) {
      this.atualizarArpDraft(payload);
    },
    onAno(value) {
      this.patch({ ano: normalizeAnoArpInput(value) });
    },
    toDate(iso) {
      return parseIsoDateLocal(iso);
    },
    toIso(date) {
      return dateToFormIsoString(date);
    },
    onVigencia(range) {
      this.patch({
        vigenciaInicio: dateToFormIsoString(range && range.start),
        vigenciaFim: dateToFormIsoString(range && range.end),
      });
    },
    onFornecedor(id) {
      if (this.fornecedorBloqueado) return;
      if (id == null || id === '') {
        this.patch({
          fornecedorId: null,
          fornecedorNome: '',
          papel: 'reserva',
        });
        return;
      }
      const fornecedorId = Number(id);
      const f = this.fornecedores.find(x => Number(x.id) === fornecedorId);
      this.patch({
        fornecedorId,
        fornecedorNome: f ? f.nome : '',
        papel: 'reserva',
      });
    },
  },
};
</script>

<style scoped>
.arp-fill__row {
  display: grid;
  grid-template-columns:
    minmax(160px, 2fr)
    minmax(88px, 0.7fr)
    minmax(72px, 0.55fr)
    minmax(200px, 1.6fr)
    auto;
  gap: 0.75rem 0.85rem;
  align-items: start;
}

.arp-fill__row >>> .field {
  margin-bottom: 0;
  width: 100%;
}

.arp-fill__row >>> .label {
  display: block;
  margin-bottom: 0.5rem;
  min-height: 1.5em;
  line-height: 1.5em;
  white-space: nowrap;
}

.arp-fill__row >>> .control,
.arp-fill__row >>> .select,
.arp-fill__row >>> .select select,
.arp-fill__row >>> .input {
  width: 100%;
  max-width: 100%;
}

.arp-fill__row >>> .select select,
.arp-fill__row >>> .input,
.arp-fill__vigencia >>> .date-range-picker__trigger {
  height: 2.5em;
}

.arp-fill__carona {
  min-width: 7.5rem;
  white-space: nowrap;
}

.arp-fill__carona >>> .control {
  display: flex;
  align-items: center;
  min-height: 2.5em;
}

.arp-fill__carona >>> .switch {
  white-space: nowrap;
}

@media (max-width: 1100px) {
  .arp-fill__row {
    grid-template-columns:
      minmax(140px, 1.5fr)
      minmax(80px, 0.6fr)
      minmax(70px, 0.5fr)
      minmax(180px, 1.4fr)
      auto;
    gap: 0.75rem 0.6rem;
  }
}

@media (max-width: 900px) {
  .arp-fill__row {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .arp-fill__fornecedor,
  .arp-fill__vigencia {
    grid-column: 1 / -1;
  }
}

@media (max-width: 520px) {
  .arp-fill__row {
    grid-template-columns: minmax(0, 1fr);
  }

  .arp-fill__fornecedor,
  .arp-fill__vigencia,
  .arp-fill__carona {
    grid-column: auto;
  }
}
</style>
